import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useModal } from "@/context/ModalContext";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { COLLECTION_PERMISSIONS, iCollection } from "@/services/collection";
import { getPaymentMethod, iPaymentMethod } from "@/services/payment";
import PaymentMethodTab from "@/pages/collection/paymentMethod/PaymentMethodTab";
import { useRbac } from "@/hooks/rbac";
import DetailsForm from "@/pages/collection/onboarding/DetailsForm";
import ImagesForm from "@/pages/collection/onboarding/ImagesForm";
import CollectionTeam from "../collection/CollectionTeam";
import CollectionSettingsModal from "../collection/CollectionSettingsModal";
import { iGroup } from "@/services/groups";
import AccountManagerCard from "./AccountManagerCard";
import CreditsModal from "../collection/CreditsModal";
import ButtonCreateNft from "../collection/ButtonCreateNft";
import CollectionSelector from "../collection/CollectionSelector";

type Props = {
  collection: iCollection;
  group: iGroup;
  onRefresh: () => void;
};

interface iMenuItemContent {
  component: ReactElement;
  title: string;
  description: string;
}

interface iMenuItem {
  icon: ReactElement;
  event: string;
  text: string;
  content: iMenuItemContent;
}

export default function DashboardSidebar(props: Props) {
  const { collection, group, onRefresh } = props;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { open } = useSidebar();
  const [selectedPage, setSelectedPage] = useState("");
  const { can, isSuperAdmin } = useRbac();

  const [pm, setPm] = useState<iPaymentMethod>();
  const [, setIsLoading] = useState(false);

  const loadCollectionData = async () => {
    if (!collection) return;

    setIsLoading(true);

    try {
      if (collection.paymentMethodId) {
        const pm = await getPaymentMethod(collection.paymentMethodId);
        setPm(pm);
      }
    } catch (err) {
      console.log("Error loading collection data", err);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadCollectionData();
  }, [collection]);

  useEffect(() => {
    const url = window.location.href.split("/");
    setSelectedPage(url[url.length - 1]);
  }, []);

  const paymentEnabled =
    collection.isActive &&
    can(COLLECTION_PERMISSIONS.view_payment_data, collection);

  const menuItems: (iMenuItem | false)[] = useMemo(() => {
    if (collection)
      return [
        can(COLLECTION_PERMISSIONS.update, collection) &&
          collection.type !== "experience" && {
            text: t("collection.hotel_basics"),
            event: "details",
            content: {
              component: (
                <DetailsForm
                  collection={collection as iCollection}
                  onRefresh={onRefresh}
                />
              ),
              title: t("collection.hotel_basics"),
              description: "",
            },
          },
        can(COLLECTION_PERMISSIONS.update, collection) &&
          collection.isActive && {
            text: t("collection.hotel_images"),
            event: "images",
            content: {
              component: (
                <ImagesForm
                  collection={collection as iCollection}
                  onRefresh={onRefresh}
                />
              ),
              title: t("collection.hotel_images"),
              description: "",
            },
          },
        can(COLLECTION_PERMISSIONS.memebers, collection) && {
          text: t("collection.members"),
          content: {
            event: "members",
            component: (
              <CollectionTeam
                onRefresh={onRefresh}
                collection={collection as iCollection}
                group={group as iGroup}
              />
            ),
            title: t("collection.members"),
            description: "",
          },
        },
        isSuperAdmin && {
          text: t("collection.settings"),
          content: {
            event: "settings",
            component: (
              <CollectionSettingsModal
                onRefresh={() => {
                  onRefresh();
                  hideModal();
                }}
                collection={collection as iCollection}
                group={group as iGroup}
              />
            ),
            title: t("collection.settings"),
            description: "",
          },
        },
        collection.takoinCredits ||
          (isSuperAdmin && {
            text: `${t("collection.service.credits_account.title")} ${
              collection.takoinCredits || 0
            }`,
            content: {
              event: "settings",
              component: (
                <CreditsModal
                  isSuperAdmin={isSuperAdmin}
                  collection={collection}
                  refresh={() => onRefresh()}
                />
              ),
              title: t("collection.service.credits_account.title"),
              description: "",
            },
          }),
        paymentEnabled && {
          text: t("collection.tab_payments"),
          content: {
            event: "payment",
            component: (
              <PaymentMethodTab
                onRefresh={onRefresh}
                collection={collection}
                pm={pm}
              ></PaymentMethodTab>
            ),
            title: t("collection.tab_payments"),
            description: t("collection.tab_payments_description"),
          },
        },
      ].filter((item): item is iMenuItem => Boolean(item));
    return [];
  }, [collection, i18n.language, pm]);

  const userPermissions = [
    can(COLLECTION_PERMISSIONS.admin, collection) && "admin", // For "Crea nuovo Tak"
    can(COLLECTION_PERMISSIONS.update, collection) && "update", // For "Dettagli Hotel"
    can(COLLECTION_PERMISSIONS.update, collection) && "images", // For "Immagini hotel"
    can(COLLECTION_PERMISSIONS.memebers, collection) && "members", // For "Team"
    can(COLLECTION_PERMISSIONS.update, collection) && "settings", // For "Impostazioni"
    can(COLLECTION_PERMISSIONS.view_payment_data, collection) && "payments", // For "Pagamenti"
  ].filter(Boolean);

  return (
    <Sidebar>
      <SidebarContent className="md:mt-8 lg:mt-14">
        <SidebarGroup>
          <SidebarGroupContent className="flex flex-col gap-1">
            <div className="mb-4 flex items-center gap-2 border-b border-black/10">
              {collection.name && (
                <CollectionSelector
                  collection={collection}
                  className="flex-1"
                ></CollectionSelector>
              )}
              {open ? <SidebarTrigger className="shrink-0" /> : null}
            </div>
            {collection.isActive &&
            collection._id !== "2dd6ece3-a4c4-47a9-98ae-818f11f5bbad" ? (
              <SidebarMenuItem
                id="taks"
                onClick={() => {
                  navigate("taks");
                  setSelectedPage("taks");
                }}
                className={`cursor-pointer flex gap-2 rounded-lg outline-1 hover:bg-takyon-black hover:bg-opacity-10  p-2 ${
                  selectedPage == "taks" ? "bg-takyon-black bg-opacity-10" : ""
                }`}
              >
                {t("dashboard_sidebar.tak")}
              </SidebarMenuItem>
            ) : null}
            {userPermissions.length > 0 && (
              <>
                {menuItems
                  .filter((item): item is iMenuItem => !!item)
                  .map((item: iMenuItem) => {
                    const menuKey = `menu-item-${item.text}`;
                    return (
                      <SidebarMenuItem
                        id={item.event}
                        key={menuKey}
                        onClick={() =>
                          showModal(item.content.component, {
                            title: item.content.title,
                            description: item.content.description,
                          })
                        }
                        className={`cursor-pointer flex gap-2 rounded-lg outline-1 hover:bg-takyon-black hover:bg-opacity-10  p-2`}
                      >
                        {item.text}
                      </SidebarMenuItem>
                    );
                  })}
              </>
            )}

            {isSuperAdmin ? <ButtonCreateNft collection={collection} /> : null}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <AccountManagerCard />
      </SidebarFooter>
    </Sidebar>
  );
}
